import axios, { AxiosError } from "axios";

// Shared API response envelope — mirrors app/schemas/base.py APIResponse.
export interface APIResponse<T> {
  success: boolean;
  data: T | null;
  error: string | null;
}

// Shared paginated envelope — mirrors app/schemas/base.py PaginatedResponse.
export interface Paginated<T> {
  success: boolean;
  data: T[];
  total: number;
  page: number;
  per_page: number;
  error: string | null;
}

export const TOKEN_KEY = "ictb_token";

// Dispatched on `window` exactly when THIS tab's own current session was
// invalidated for real (see handleResponseError below) — never for a 401
// belonging to some other/stale token. AuthContext listens for this to
// surface an honest "your session ended" state instead of silently failing.
export const SESSION_ENDED_EVENT = "ictb:session-ended";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// beads F3: a request the network silently drops (no RST, no error, no
// close — connectionTest.ts's own comment documents this live-verified
// against prod for the SIP WSS port; the same class of failure applies here)
// would otherwise hang forever with no default axios/XHR timeout at all.
// This bounds every request through this client, including the agentLogin()/
// getSipCredentials() calls in AgentConsole's own "go online" chain, so a
// dead network surfaces as a rejected promise instead of an infinite spinner.
// Generous enough for the largest ordinary payload this client carries
// (chunked contact-list uploads, api/contacts.ts) without changing behavior
// for the common case, where every real request settles in well under a
// second.
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

export const api = axios.create({
  baseURL: "/api",
  headers: { "Content-Type": "application/json" },
  timeout: DEFAULT_REQUEST_TIMEOUT_MS,
});

// Attach the JWT as a Bearer token on every request.
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Pulls the bearer token, if any, off the request that produced this error.
// Used to tell "my current session just died" apart from "some other tab's
// old/superseded token 401'd" — see handleResponseError.
function bearerTokenFrom(error: AxiosError): string | null {
  const header = error.config?.headers?.Authorization;
  if (typeof header !== "string") return null;
  const match = header.match(/^Bearer (.+)$/);
  return match ? match[1] : null;
}

// On 401, clear the token ONLY when the failing request actually carried the
// token this tab currently has in storage.
//
// localStorage is shared by every tab on the origin. Before this fix, ANY
// 401 — from ANY tab — wiped it, which meant a stale tab still polling with
// an old/superseded token could sign out a DIFFERENT tab that had just
// authenticated successfully (beads_php_master_phone_system-bar). A 401 with
// no Authorization header at all is an unauthenticated request; that is
// never evidence a real session died, so it must not clear anything either.
export function handleResponseError(error: AxiosError): Promise<never> {
  if (error.response?.status === 401) {
    const sentToken = bearerTokenFrom(error);
    const storedToken = getToken();
    if (sentToken && storedToken && sentToken === storedToken) {
      setToken(null);
      window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
    }
  }
  return Promise.reject(error);
}

api.interceptors.response.use((resp) => resp, handleResponseError);

// Unwrap an APIResponse<T>, throwing on failure so callers/React Query see an error.
export function unwrap<T>(resp: APIResponse<T>): T {
  if (!resp.success || resp.data === null) {
    throw new Error(resp.error || "Request failed");
  }
  return resp.data;
}
